import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { authService } from '../../services/auth.service';
import { API_ENDPOINTS } from '../../config/api';
import { motion, AnimatePresence } from 'framer-motion';
import countriesService, { Country, Wilaya } from '../../services/countries.service';
import { 
  User, 
  Building2, 
  MapPin, 
  Globe, 
  Facebook, 
  Linkedin,
  Edit,
  Save,
  X,
  Camera,
  Shield,
  MessageSquare,
  Trash2,
  AlertTriangle
} from 'lucide-react';

interface ConsultantProfile {
  id: number;
  name: string;
  company_name?: string;
  phone_number: string;
  about?: string;
  address?: string;
  website?: string;
  facebook_url?: string;
  linkedin_url?: string;
  logo?: string;
  category_id?: string;
  country?: string;
  country_id?: number;
  wilaya_id?: number | null;
  city?: string;
}


const ConsultantProfilePage: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<ConsultantProfile | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState<Partial<ConsultantProfile>>({});
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [countries, setCountries] = useState<Country[]>([]);
  const [wilayas, setWilayas] = useState<Wilaya[]>([]);
  const [loadingCountries, setLoadingCountries] = useState(true);
  const [loadingWilayas, setLoadingWilayas] = useState(false);

  useEffect(() => {
    const loadProfile = async () => {
      try {
        setIsLoading(true);
        
        // Load profile data and countries in parallel
        const [profileData, countriesData] = await Promise.all([
          authService.getTrainingCenterInfo(),
          countriesService.getCountries()
        ]);
        
        setCountries(countriesData);
        setProfile(profileData);
        
        // Set form data with proper defaults
        const formDataWithDefaults = {
          ...profileData,
          wilaya_id: profileData.wilaya_id || null,
          city: profileData.city || '',
          country_id: profileData.country_id || (typeof profileData.country === 'string' ? undefined : profileData.country_id)
        };
        setFormData(formDataWithDefaults);
        
        // Load wilayas if country_id is set and country is Algeria
        if (formDataWithDefaults.country_id) {
          const selectedCountry = countriesData.find(c => c.id === formDataWithDefaults.country_id);
          if (selectedCountry && selectedCountry.name === 'Algeria') {
            try {
              const algerianWilayas = await countriesService.getWilayas(formDataWithDefaults.country_id);
              setWilayas(algerianWilayas);
            } catch (error) {
            }
          }
        }
        
        // Handle logo display
        if (profileData.logo) {
          if (profileData.logo.startsWith('data:')) {
            setLogoPreview(profileData.logo);
          } else {
            const logoUrl = `${API_ENDPOINTS.UPLOADS}/${profileData.logo}`;
            setLogoPreview(logoUrl);
          }
        }
        
      } catch (error) {
      } finally {
        setIsLoading(false);
        setLoadingCountries(false);
      }
    };

    loadProfile();
  }, []);

  // Load wilayas when country changes to Algeria
  useEffect(() => {
    const loadWilayas = async () => {
      if (formData.country_id) {
        const selectedCountry = countries.find(c => c.id === formData.country_id);
        if (selectedCountry && selectedCountry.name === 'Algeria') {
          setLoadingWilayas(true);
          try {
            const algerianWilayas = await countriesService.getWilayas(formData.country_id);
            setWilayas(algerianWilayas);
          } catch (error) {
          } finally {
            setLoadingWilayas(false);
          }
        } else {
          setWilayas([]);
          // Reset wilaya_id if not Algeria
          if (formData.wilaya_id) {
            setFormData(prev => ({ ...prev, wilaya_id: null }));
          }
        }
      } else {
        setWilayas([]);
      }
    };

    loadWilayas();
  }, [formData.country_id, formData.wilaya_id, countries]);

  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => {
      if (logoPreview && logoPreview.startsWith('blob:')) {
        URL.revokeObjectURL(logoPreview);
      }
    };
  }, [logoPreview]);

  const handleInputChange = (field: keyof ConsultantProfile, value: string | number | null) => {
    // Handle country_id change - reset wilaya if changing from Algeria
    if (field === 'country_id') {
      const countryId = value ? (typeof value === 'number' ? value : parseInt(value as string)) : undefined;
      const selectedCountry = countries.find(c => c.id === countryId);
      setFormData(prev => ({
        ...prev,
        [field]: countryId,
        wilaya_id: selectedCountry?.name === 'Algeria' ? prev.wilaya_id : null,
        city: selectedCountry?.name === 'Algeria' ? (prev.city || '') : prev.city
      }));
    } else if (field === 'wilaya_id') {
      setFormData(prev => ({
        ...prev,
        [field]: value ? (typeof value === 'number' ? value : parseInt(value as string)) : null
      }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Check file size (5MB limit)
      const maxSize = 5 * 1024 * 1024; // 5MB in bytes
      if (file.size > maxSize) {
        alert('Le fichier est trop volumineux. Veuillez choisir une image de moins de 5MB.');
        return;
      }
      
      // Check file type
      const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        alert('Format de fichier invalide. Veuillez utiliser une image (PNG, JPG, GIF).');
        return;
      }
      
      // Clean up previous blob URL
      if (logoPreview && logoPreview.startsWith('blob:')) {
        URL.revokeObjectURL(logoPreview);
      }
      
      setLogoFile(file);
      // Create preview URL for display only
      const previewUrl = URL.createObjectURL(file);
      setLogoPreview(previewUrl);
      // Don't set blob URL in form data - let backend handle the file
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    
    try {
      // Prepare update data - only include fields that should be sent
      const updateData: any = {
        name: formData.name,
        company_name: formData.company_name,
        phone_number: formData.phone_number,
        about: formData.about,
        address: formData.address,
        website: formData.website || null,
        // Always include social media URLs - send empty string if cleared, null if not set
        facebook_url: formData.facebook_url !== undefined ? (formData.facebook_url === '' ? '' : formData.facebook_url || null) : null,
        linkedin_url: formData.linkedin_url !== undefined ? (formData.linkedin_url === '' ? '' : formData.linkedin_url || null) : null,
        category_id: formData.category_id,
        country_id: formData.country_id,
        wilaya_id: formData.wilaya_id || null,
        city: formData.city || null
      };
      
      // Handle logo file separately if a new file is selected
      if (logoFile) {
        updateData.logo = logoFile; // This will be handled by the auth service
      }
      
      await authService.updateTrainingCenter(updateData);
      
      // Reload profile from server to ensure we have the latest saved data
      const updatedProfile = await authService.getTrainingCenterInfo();
      setProfile(updatedProfile);
      
      // Update formData with the fresh profile data
      const formDataWithDefaults = {
        ...updatedProfile,
        wilaya_id: updatedProfile.wilaya_id || null,
        city: updatedProfile.city || '',
        country_id: updatedProfile.country_id || (typeof updatedProfile.country === 'string' ? undefined : updatedProfile.country_id)
      };
      setFormData(formDataWithDefaults);
      
      // Update logo preview if needed
      if (updatedProfile.logo) {
        if (updatedProfile.logo.startsWith('data:')) {
          setLogoPreview(updatedProfile.logo);
        } else {
          const logoUrl = `${API_ENDPOINTS.UPLOADS}/${updatedProfile.logo}`;
          setLogoPreview(logoUrl);
        }
      }
      
      // Clear logo file since it's been saved
      setLogoFile(null);
      
      setIsEditing(false);
    } catch (error: any) {
      
      // Extract user-friendly error message
      let errorMessage = 'Erreur lors de la sauvegarde du profil';
      
      if (error.message) {
        if (error.message.includes('File too large')) {
          errorMessage = 'Le fichier est trop volumineux. Veuillez choisir une image de moins de 5MB.';
        } else if (error.message.includes('Invalid file format')) {
          errorMessage = 'Format de fichier invalide. Veuillez utiliser une image (PNG, JPG, GIF).';
        } else if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else {
          errorMessage = error.message;
        }
      }
      
      alert(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setFormData(profile || {});
    setLogoFile(null);
    
    // Reset logo preview to original
    if (profile?.logo) {
      if (profile.logo.startsWith('data:')) {
        setLogoPreview(profile.logo);
      } else {
        setLogoPreview(`${API_ENDPOINTS.UPLOADS}/${profile.logo}`);
      }
    } else {
      setLogoPreview(null);
    }
    
    setIsEditing(false);
  };

  const handleDeleteAccount = async () => {
    try {
      setIsDeleting(true);
      await authService.deleteConsultantAccount();
      // Clear auth data and logout
      logout();
      // Navigate to home page
      navigate('/');
    } catch (error) {
      alert('Failed to delete account. Please try again.');
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-brand-surface via-white to-purple-50 p-4 sm:p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-surface via-white to-purple-50 p-4 sm:p-6">
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="bg-gradient-to-r from-brand-primary via-brand-primary/90 to-brand-secondary/80 rounded-2xl p-8 text-white shadow-xl"
        >
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
            <div>
              <div className="flex items-center gap-3 mb-3">
                <div className="p-3 bg-white/20 rounded-xl backdrop-blur-sm">
                  <User className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
                </div>
                <h1 className="text-2xl sm:text-4xl font-bold">
                  Mon Profil Consultant
                </h1>
              </div>
              <p className="text-white/90 text-base sm:text-lg">
                Gérez vos informations personnelles et professionnelles
              </p>
            </div>
            <div className="flex items-center gap-3 mt-6 lg:mt-0">
              <AnimatePresence mode="wait">
                {isEditing ? (
                  <motion.div
                    key="editing"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    className="flex items-center gap-2"
                  >
                    <button
                      onClick={handleCancel}
                      className="inline-flex items-center px-4 py-2 bg-white/20 backdrop-blur-sm text-white font-medium rounded-lg hover:bg-white/30 transition-all duration-200"
                    >
                      <X className="h-4 w-4 mr-2" />
                      Annuler
                    </button>
                    <button
                      onClick={handleSave}
                      disabled={isSaving}
                      className="inline-flex items-center px-4 py-2 bg-white text-brand-primary font-medium rounded-lg hover:bg-white/90 disabled:opacity-50 transition-all duration-200 shadow-lg"
                    >
                      <Save className="h-4 w-4 mr-2" />
                      {isSaving ? 'Sauvegarde...' : 'Sauvegarder'}
                    </button>
                  </motion.div>
                ) : (
                  <motion.button
                    key="edit"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    onClick={() => setIsEditing(true)}
                    className="inline-flex items-center px-6 py-3 bg-white/20 backdrop-blur-sm text-white font-medium rounded-lg hover:bg-white/30 transition-all duration-200 shadow-lg hover:shadow-xl"
                  >
                    <Edit className="h-5 w-5 mr-2" />
                    Modifier le Profil
                  </motion.button>
                )}
              </AnimatePresence>
            </div>
          </div>
        </motion.div>


        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Profile Card */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4, duration: 0.6 }}
            className="lg:col-span-1"
          >
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
              <div className="text-center">
                <div className="relative inline-block">
                  {logoPreview ? (
                    <img
                      src={logoPreview}
                      alt="Logo"
                      className="h-32 w-32 rounded-full object-cover border-4 border-brand-primary/20 shadow-lg"
                      onError={(e) => {
                      }}
                      onLoad={() => {
                      }}
                    />
                  ) : (
                    <div className="h-32 w-32 rounded-full bg-gradient-to-br from-brand-primary/20 to-brand-secondary/20 flex items-center justify-center border-4 border-brand-primary/20 shadow-lg">
                      <User className="h-16 w-16 text-brand-primary" />
                    </div>
                  )}
                  {isEditing && (
                    <label className="absolute bottom-2 right-2 bg-brand-primary text-white rounded-full p-3 cursor-pointer hover:bg-brand-primary/90 transition-all duration-200 shadow-lg hover:shadow-xl">
                      <Camera className="h-5 w-5" />
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleLogoChange}
                        className="hidden"
                      />
                    </label>
                  )}
                </div>
                
                <h2 className="mt-6 text-2xl font-bold text-gray-900">
                  {profile?.company_name || profile?.name || 'Consultant'}
                </h2>
                <p className="text-gray-600 mt-2">Consultant Professionnel</p>
                
                {/* Social Media Links */}
                <div className="mt-6 flex justify-center space-x-4">
                  {profile?.website && (
                    <a
                      href={profile.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-3 bg-gray-100 text-gray-600 rounded-full hover:bg-brand-primary hover:text-white transition-all duration-200 hover:scale-110"
                    >
                      <Globe className="h-5 w-5" />
                    </a>
                  )}
                  {profile?.facebook_url && typeof profile.facebook_url === 'string' && profile.facebook_url.trim() !== '' && (
                    <a
                      href={profile.facebook_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-3 bg-gray-100 text-gray-600 rounded-full hover:bg-blue-600 hover:text-white transition-all duration-200 hover:scale-110"
                    >
                      <Facebook className="h-5 w-5" />
                    </a>
                  )}
                  {profile?.linkedin_url && typeof profile.linkedin_url === 'string' && profile.linkedin_url.trim() !== '' && (
                    <a
                      href={profile.linkedin_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-3 bg-gray-100 text-gray-600 rounded-full hover:bg-blue-700 hover:text-white transition-all duration-200 hover:scale-110"
                    >
                      <Linkedin className="h-5 w-5" />
                    </a>
                  )}
                </div>

                {/* Status Badge */}
                <div className="mt-6">
                  <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-emerald-100 to-emerald-200 text-emerald-800 rounded-full text-sm font-medium">
                    <Shield className="h-4 w-4 mr-2" />
                    Profil Vérifié
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Profile Details */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.6, duration: 0.6 }}
            className="lg:col-span-2 space-y-6"
          >
            {/* Basic Information */}
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <div className="p-2 bg-brand-primary/20 rounded-lg">
                  <Building2 className="h-5 w-5 text-brand-primary" />
                </div>
                Informations Professionnelles
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Company Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nom de l'entreprise
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={formData.company_name || formData.name || ''}
                      onChange={(e) => handleInputChange('company_name', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-brand-primary transition-all duration-200"
                    />
                  ) : (
                    <p className="text-gray-900 font-medium">{profile?.company_name || profile?.name || 'Non spécifié'}</p>
                  )}
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Téléphone
                  </label>
                  {isEditing ? (
                    <input
                      type="tel"
                      value={formData.phone_number || ''}
                      onChange={(e) => handleInputChange('phone_number', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-brand-primary transition-all duration-200"
                    />
                  ) : (
                    <p className="text-gray-900 font-medium">{profile?.phone_number || 'Non spécifié'}</p>
                  )}
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email
                  </label>
                  <p className="text-gray-900 font-medium">{user?.email || 'Non spécifié'}</p>
                  <p className="text-xs text-gray-500 mt-1">L'email ne peut pas être modifié</p>
                </div>

                {/* Website */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Site web
                  </label>
                  {isEditing ? (
                    <input
                      type="url"
                      value={formData.website || ''}
                      onChange={(e) => handleInputChange('website', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-brand-primary transition-all duration-200"
                      placeholder="https://www.example.com"
                    />
                  ) : (
                    <p className="text-gray-900 font-medium">{profile?.website || 'Non spécifié'}</p>
                  )}
                </div>

                {/* Location Section */}
                <div className="md:col-span-2 space-y-4">
                  <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                    <MapPin className="h-4 w-4" />
                    Localisation
                  </div>

                  {/* Country */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Pays
                    </label>
                    {isEditing ? (
                      <select
                        value={formData.country_id || ''}
                        onChange={(e) => handleInputChange('country_id', e.target.value)}
                        disabled={loadingCountries}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-brand-primary transition-all duration-200 disabled:bg-gray-100"
                      >
                        <option value="">
                          {loadingCountries ? 'Chargement des pays...' : 'Sélectionner un pays'}
                        </option>
                        {countries.map(country => (
                          <option key={country.id} value={country.id}>
                            {country.name}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <p className="text-gray-900 font-medium">
                        {countries.find(c => c.id === profile?.country_id)?.name || profile?.country || 'Non spécifié'}
                      </p>
                    )}
                  </div>

                  {/* Wilaya Selection - Only show if Algeria is selected */}
                  {isEditing && formData.country_id && countries.find(c => c.id === formData.country_id)?.name === 'Algeria' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Wilaya
                      </label>
                      <select
                        value={formData.wilaya_id || ''}
                        onChange={(e) => handleInputChange('wilaya_id', e.target.value)}
                        disabled={loadingWilayas}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-brand-primary transition-all duration-200 disabled:bg-gray-100"
                      >
                        <option value="">
                          {loadingWilayas ? 'Chargement des wilayas...' : 'Sélectionner une wilaya'}
                        </option>
                        {wilayas.map((wilaya) => (
                          <option key={wilaya.id} value={wilaya.id}>
                            {wilaya.id.toString().padStart(2, '0')} - {wilaya.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* City Input - Only show if not Algeria */}
                  {isEditing && formData.country_id && countries.find(c => c.id === formData.country_id)?.name !== 'Algeria' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Ville
                      </label>
                      <input
                        type="text"
                        value={formData.city || ''}
                        onChange={(e) => handleInputChange('city', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-brand-primary transition-all duration-200"
                        placeholder="Nom de la ville"
                      />
                    </div>
                  )}

                  {/* Display wilaya or city in view mode */}
                  {!isEditing && (
                    <>
                      {profile?.wilaya_id && profile.country_id && countries.find(c => c.id === profile.country_id)?.name === 'Algeria' && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Wilaya
                          </label>
                          <p className="text-gray-900 font-medium">
                            {wilayas.find(w => w.id === profile.wilaya_id) 
                              ? `${profile.wilaya_id.toString().padStart(2, '0')} - ${wilayas.find(w => w.id === profile.wilaya_id)?.name || ''}`
                              : profile.wilaya_id.toString().padStart(2, '0')}
                          </p>
                        </div>
                      )}
                      {profile?.city && (!profile.wilaya_id || !profile.country_id || countries.find(c => c.id === profile.country_id)?.name !== 'Algeria') && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Ville
                          </label>
                          <p className="text-gray-900 font-medium">{profile.city}</p>
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* Address */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Adresse
                  </label>
                  {isEditing ? (
                    <textarea
                      value={formData.address || ''}
                      onChange={(e) => handleInputChange('address', e.target.value)}
                      rows={3}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-brand-primary transition-all duration-200"
                    />
                  ) : (
                    <p className="text-gray-900 font-medium">{profile?.address || 'Non spécifié'}</p>
                  )}
                </div>

                {/* About */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    À propos
                  </label>
                  {isEditing ? (
                    <textarea
                      value={formData.about || ''}
                      onChange={(e) => handleInputChange('about', e.target.value)}
                      rows={4}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-brand-primary transition-all duration-200"
                      placeholder="Décrivez votre expertise et vos services..."
                    />
                  ) : (
                    <p className="text-gray-900 leading-relaxed break-words whitespace-pre-wrap">{profile?.about || 'Non spécifié'}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Social Media Section */}
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <div className="p-2 bg-brand-secondary/20 rounded-lg">
                  <MessageSquare className="h-5 w-5 text-brand-secondary" />
                </div>
                Réseaux Sociaux
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Facebook
                  </label>
                  {isEditing ? (
                    <input
                      type="url"
                      value={formData.facebook_url || ''}
                      onChange={(e) => handleInputChange('facebook_url', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-brand-primary transition-all duration-200"
                      placeholder="https://facebook.com/yourpage"
                    />
                  ) : (
                    <p className="text-gray-900 font-medium">
                      {profile?.facebook_url && typeof profile.facebook_url === 'string' && profile.facebook_url.trim() !== '' 
                        ? profile.facebook_url 
                        : 'Non spécifié'}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    LinkedIn
                  </label>
                  {isEditing ? (
                    <input
                      type="url"
                      value={formData.linkedin_url || ''}
                      onChange={(e) => handleInputChange('linkedin_url', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-brand-primary transition-all duration-200"
                      placeholder="https://linkedin.com/in/yourprofile"
                    />
                  ) : (
                    <p className="text-gray-900 font-medium">
                      {profile?.linkedin_url && typeof profile.linkedin_url === 'string' && profile.linkedin_url.trim() !== '' 
                        ? profile.linkedin_url 
                        : 'Non spécifié'}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Delete Account Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8, duration: 0.6 }}
              className="bg-white rounded-2xl shadow-xl border border-red-200 p-8 mt-6"
            >
              <div className="bg-red-50 border border-red-200 rounded-xl p-6">
                <div className="flex items-start space-x-3">
                  <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-red-900 mb-2">
                      Zone de Danger
                    </h3>
                    <p className="text-sm text-red-700 mb-4">
                      Une fois que vous supprimez votre compte, il n'y a pas de retour en arrière. Toutes vos données, y compris vos services, révisions et informations personnelles seront définitivement supprimées.
                    </p>
                    <button
                      onClick={() => setShowDeleteConfirm(true)}
                      disabled={isDeleting}
                      className="bg-red-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-red-700 transition-all shadow-md flex items-center gap-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Trash2 className="h-4 w-4" />
                      {isDeleting ? 'Suppression...' : 'Supprimer mon compte'}
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6"
          >
            <div className="flex items-center space-x-3 mb-4">
              <div className="bg-red-100 p-3 rounded-full">
                <AlertTriangle className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900">
                Supprimer le compte?
              </h3>
            </div>
            
            <p className="text-gray-700 mb-6">
              Êtes-vous absolument sûr de vouloir supprimer votre compte? Cette action ne peut pas être annulée et supprimera définitivement:
            </p>
            
            <ul className="list-disc list-inside space-y-2 mb-6 text-sm text-gray-600">
              <li>Toutes vos informations personnelles</li>
              <li>Vos services de consultation</li>
              <li>Vos créneaux de disponibilité</li>
              <li>Vos avis et évaluations</li>
              <li>Toutes les données associées</li>
            </ul>
            
            <p className="text-sm font-semibold text-red-600 mb-6">
              Cette action est permanente et ne peut pas être inversée!
            </p>
            
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isDeleting}
                className="flex-1 bg-gray-200 text-gray-800 px-4 py-3 rounded-lg font-medium hover:bg-gray-300 transition-all disabled:opacity-50"
              >
                Annuler
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={isDeleting}
                className="flex-1 bg-red-600 text-white px-4 py-3 rounded-lg font-medium hover:bg-red-700 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isDeleting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Suppression...
                  </>
                ) : (
                  <>
                    <Trash2 className="h-4 w-4" />
                    Oui, supprimer le compte
                  </>
                )}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default ConsultantProfilePage;
